import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { NXGDSStatusTagVariants, type NXGDSTableRow } from '@nsftx/seven-nxg-design-system'
import {
  useGamesStore,
  useTicketsStore,
  getGameDisplayName,
  type ClientTicketActionType,
  type LocalTicketStatuses,
} from '@nsftx/seven-client-retail-vue-sgp'
import { formatNumber } from '@nsftx/seven-nxg-retail-vue-sdk'
import { useSettingsStore } from '@/stores/settings/settingsStore'
import { useGameConfigurationStore } from '@/stores/cms/gameConfigurationStore'

export interface LatestBetslipsDataTable extends NXGDSTableRow {
  feBetslipId: string
  indicator: string
  game: string
  id: string
  time: number
  action: string
  amount: string
  status: {
    label: string
    variant: NXGDSStatusTagVariants
  }
}

export const useLatestBetslipsDataTable = () => {
  const route = useRoute<'game'>()
  const { tickets: betslips } = storeToRefs(useTicketsStore())
  const { t } = useI18n({ useScope: 'global' })
  const { getGameLinkByCode } = useGamesStore()
  const { lastTicketsSettings } = storeToRefs(useSettingsStore())
  const { getByGameId } = useGameConfigurationStore()

  const actionTranslations = computed<Partial<Record<ClientTicketActionType, string>>>(() => ({
    BETSLIP_PAYIN: t('slip_payin'),
  }))

  const latestBetslipsHeaders = computed(() => [
    { key: 'indicator', label: '' },
    { key: 'game', label: t('slip_table_game') },
    { key: 'id', label: t('slip_table_id') },
    { key: 'time', label: t('slip_table_time') },
    { key: 'action', label: t('slip_table_action') },
    { key: 'amount', label: t('slip_table_amount') },
    { key: 'status', label: t('slip_table_status') },
  ])

  const statusLabels = computed<Record<LocalTicketStatuses, string>>(() => ({
    PENDING: t('slip_status_pending') + '...',
    ACCEPTED: t('slip_status_accepted'),
    FAILED: t('slip_status_failed'),
    REJECTED: t('slip_status_rejected'),
  }))

  const statusVariants = computed<Record<LocalTicketStatuses, NXGDSStatusTagVariants>>(() => ({
    PENDING: 'subtle',
    ACCEPTED: 'positive',
    FAILED: 'negative',
    REJECTED: 'negative',
  }))

  const latestBetslipsDataTable = computed<LatestBetslipsDataTable[]>(() =>
    betslips.value
      .slice(0, Number(lastTicketsSettings.value.numberOfBetslips))
      .filter((betslip) =>
        lastTicketsSettings.value.bestlipsFromGame === 'show_all'
          ? true
          : betslip.linkCode === route.params.gameId,
      )
      .map((betslip) => {
        const gameLink = getGameLinkByCode(betslip.linkCode)
        const gameDisplayName = gameLink ? getGameDisplayName(gameLink) : '-'
        const betslipDisplayId = getBetslipDisplayId(betslip.barcode)
        const indicator = getByGameId(gameLink?.installation.game.id)?.color ?? ''
        const status = betslip.localStatus

        return {
          feBetslipId: betslip.feBetslipId,
          indicator,
          game: gameDisplayName,
          id: betslipDisplayId,
          time: betslip.timestamp,
          action: getTranslationByAction(betslip.actionType),
          amount:
            betslip.amount && betslip.currency
              ? getFormattedAmount(betslip.amount, betslip.currency)
              : '-',
          status: {
            label: status ? statusLabels.value[status] : '-',
            variant: status ? statusVariants.value[status] : 'subtle',
          },
        }
      }),
  )

  const getTranslationByAction = (actionType: ClientTicketActionType) =>
    actionTranslations.value[actionType] ?? '-'

  const getBetslipDisplayId = (barcode: string | null) =>
    barcode ? `...${barcode.slice(-6).toUpperCase()}` : '-'

  const getFormattedAmount = (amount: number, currency: string) =>
    formatNumber(amount, {
      style: 'currency',
      currencySymbol: currency,
    })

  return {
    latestBetslipsDataTable,
    latestBetslipsHeaders,
  }
}
